/**
 * app.repuagency.com (and other *.repuagency.com hosts) — not mini-app product domains.
 * Matches Subscription.vue `isRepuagencySite` / agency-guard billing wall host check.
 */
export function isRepuagencyProductHost() {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return false
  }
  return window.location.hostname.includes('repuagency.com')
}

/**
 * True when the agency has RepuAgency product access.
 * Mini-app-only plans (ReplyEngine, Social Review, etc.) do not count.
 *
 * @param {Object} user - User object from store
 * @returns {boolean}
 */
export function hasRepuAgencyAccess(user) {
  if (!user || !user.agency) {
    return false
  }

  if (user.agency.free_fee) {
    return true
  }

  return !!user.has_repu_agency
}

/**
 * First-time RepuAgency customer (never purchased RA). Mini-app history does not count.
 * ReplyEngine / TapEngine owners should still get the RA free trial.
 */
export function isRepuAgencyTrialEligible(user) {
  if (!user || hasRepuAgencyAccess(user) || user.on_trial) {
    return false
  }
  if (user.repuagency_trial_eligible === true) return true
  if (user.repuagency_trial_eligible === false) return false
  const plans = user.user_plans || []
  return !plans.some((row) => {
    const db = row && row.plan
    if (!db) return false
    const pivotPt = String(row.product_type || '')
    const dbPt = String(db.product_type || '')
    if (pivotPt === 'RepuAgency' || dbPt === 'RepuAgency') return true
    const products = db.planProducts
    return Array.isArray(products) && products.includes('RepuAgency')
  })
}

/**
 * Check if an agency user is subscribed.
 * Broad agency subscription signal for routing (not the same as billing recovery /
 * Stripe past_due, which lives in agencyBillingRecoveryRedirect).
 *
 * @param {Object} user - User object from store
 * @returns {boolean} - True if user is subscribed
 */
export function isAgencySubscribed(user) {
  if (!user || user.role !== 'Agency' || !user.agency) {
    return false
  }

  if (user.agency.free_fee) {
    return true
  }

  if (user.subscribed) {
    return true
  }

  if (user.user_plans && Array.isArray(user.user_plans)) {
    return user.user_plans.some((plan) => !plan.past_due)
  }

  return false
}

/**
 * Check if user has access to a specific product
 * @param {Object} user - User object from store
 * @param {string} productType - Product type to check (e.g., 'ReplyEngine', 'SocialReview')
 * @returns {boolean} - True if user has access to the product
 */
export function hasProductAccess(user, productType) {
  if (!user || !user.agency) {
    return false
  }

  if (user.agency.free_fee) {
    return true
  }

  if (user.user_plans && Array.isArray(user.user_plans)) {
    return user.user_plans.some((plan) => {
      return (
        !plan.past_due &&
        (plan.plan.product_type === productType ||
          (Array.isArray(plan.plan.planProducts) &&
            plan.plan.planProducts.includes(productType)))
      )
    })
  }

  return false
}

function planProductType(plan) {
  return (plan && plan.db_plan && plan.db_plan.product_type) || (plan && plan.product_type) || null
}

/**
 * Currently marketed RepuAgency monthly plans (Stripe Visible=true).
 * Retired SKUs such as Mini Apps Combined stay active in Stripe but must not be offered.
 */
export function isVisibleRepuAgencyMonthlyStorefrontPlan(plan) {
  if (!plan || !plan.id || plan.visible !== true) return false
  if (planProductType(plan) !== 'RepuAgency') return false
  if (plan.type === 'one_time') return false
  return (
    !plan.recurring ||
    (plan.recurring.interval === 'month' && Number(plan.recurring.interval_count || 1) === 1) ||
    plan.type === 'month' ||
    plan.type === 'recurring'
  )
}

/**
 * Recovery picker: the account's previous plan first (including retired founder SKUs),
 * then currently sold monthly plans. Hidden founder prices stay off public signup.
 */
export function buildRepuAgencyRecoveryPlanChoices(storefrontPlans, previousPlan) {
  const choices = []
  const seen = new Set()
  const add = (plan) => {
    if (!plan || !plan.id || seen.has(String(plan.id))) return
    const amount = plan.amount !== undefined && plan.amount !== null ? String(plan.amount) : ''
    if (!amount) return
    seen.add(String(plan.id))
    choices.push({
      id: plan.id,
      name: plan.name || 'Plan',
      desc: plan.desc || '',
      amount,
    })
  }

  const storefront = (storefrontPlans || []).filter(isVisibleRepuAgencyMonthlyStorefrontPlan)
  if (previousPlan && previousPlan.id) {
    add(previousPlan)
  }
  storefront.forEach(add)
  return choices
}

export function defaultRecoverySubscribePlanId(choices, previousPlanId) {
  const list = choices || []
  if (previousPlanId && list.some((plan) => String(plan.id) === String(previousPlanId))) {
    return previousPlanId
  }
  return list[0] ? list[0].id : null
}

export function recoverySubscribePlanId(choices, selectedPlanId) {
  const list = choices || []
  if (selectedPlanId && list.some((plan) => String(plan.id) === String(selectedPlanId))) {
    return selectedPlanId
  }
  return list[0] ? list[0].id : null
}
